import { useState } from "react";
import {
  Settings,
  Edit2,
  Check,
  X,
  List,
  LayoutGrid,
  ChevronsUpDown,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import type {
  VideoHubState,
  VideoHubInput,
  VideoHubOutput,
} from "@production-hub/shared";

type RoutingView = "list" | "buttons";

interface VideoHubPanelProps {
  state: VideoHubState;
  onRoute?: (output: number, input: number) => void;
  onLabelInput?: (index: number, label: string) => void;
  onLabelOutput?: (index: number, label: string) => void;
  isLoading?: boolean;
}

export const VideoHubPanel = ({
  state,
  onRoute,
  onLabelInput,
  onLabelOutput,
  isLoading,
}: VideoHubPanelProps) => {
  const [routingView, setRoutingView] = useState<RoutingView>("list");

  return (
    <div className="space-y-4">
      {/* Device Info */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{state.model}</h3>
          <p className="text-sm text-muted-foreground">
            {state.inputs.length} inputs / {state.outputs.length} outputs
          </p>
        </div>
        <Badge variant="outline" className="gap-1">
          <Settings className="h-3 w-3" />
          VideoHub
        </Badge>
      </div>

      <Tabs defaultValue="routing">
        <TabsList>
          <TabsTrigger value="routing">Routing</TabsTrigger>
          <TabsTrigger value="inputs">Inputs</TabsTrigger>
          <TabsTrigger value="outputs">Outputs</TabsTrigger>
        </TabsList>

        {/* Routing Tab */}
        <TabsContent value="routing" className="mt-3">
          {/* View toggle */}
          <div className="flex justify-end mb-2">
            <div className="flex border rounded-md">
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "h-7 px-2 rounded-r-none",
                  routingView === "list" && "bg-accent",
                )}
                onClick={() => setRoutingView("list")}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "h-7 px-2 rounded-l-none",
                  routingView === "buttons" && "bg-accent",
                )}
                onClick={() => setRoutingView("buttons")}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {routingView === "list" ? (
            <RoutingList
              inputs={state.inputs}
              outputs={state.outputs}
              onRoute={onRoute}
              disabled={isLoading}
            />
          ) : (
            <RoutingButtonPanel
              inputs={state.inputs}
              outputs={state.outputs}
              onRoute={onRoute}
              disabled={isLoading}
            />
          )}
        </TabsContent>

        {/* Input Labels Tab */}
        <TabsContent value="inputs" className="mt-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Input Labels</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">#</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {state.inputs.map((input) => (
                    <LabelRow
                      key={input.index}
                      index={input.index}
                      label={input.label}
                      onSave={onLabelInput}
                      disabled={isLoading}
                    />
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Output Labels Tab */}
        <TabsContent value="outputs" className="mt-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Output Labels</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">#</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {state.outputs.map((output) => (
                    <LabelRow
                      key={output.index}
                      index={output.index}
                      label={output.label}
                      onSave={onLabelOutput}
                      disabled={isLoading}
                    />
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* List View — Compact rows with searchable combobox           */
/* ─────────────────────────────────────────────────────────── */

interface RoutingListProps {
  inputs: VideoHubInput[];
  outputs: VideoHubOutput[];
  onRoute?: (output: number, input: number) => void;
  disabled?: boolean;
}

const RoutingList = ({
  inputs,
  outputs,
  onRoute,
  disabled,
}: RoutingListProps) => (
  <Card>
    <CardContent className="p-0">
      <div className="divide-y">
        {outputs.map((output) => (
          <RoutingRow
            key={output.index}
            output={output}
            inputs={inputs}
            currentSource={inputs.find((i) => i.index === output.source)}
            onRoute={onRoute}
            disabled={disabled}
          />
        ))}
      </div>
    </CardContent>
  </Card>
);

interface RoutingRowProps {
  output: VideoHubOutput;
  inputs: VideoHubInput[];
  currentSource?: VideoHubInput;
  onRoute?: (output: number, input: number) => void;
  disabled?: boolean;
}

const RoutingRow = ({
  output,
  inputs,
  currentSource,
  onRoute,
  disabled,
}: RoutingRowProps) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex items-center gap-2 px-3 py-1.5">
      <span className="text-xs font-medium truncate min-w-0 flex-1">
        {output.label}
      </span>
      <span className="text-muted-foreground text-xs">←</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-[140px] sm:w-[180px] justify-between text-xs font-normal px-2"
            disabled={disabled || !onRoute}
          >
            <span className="truncate">
              {currentSource?.label ?? `Input ${output.source + 1}`}
            </span>
            <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50 ml-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" align="end">
          <Command>
            <CommandInput
              placeholder="Search input..."
              className="h-8 text-xs"
            />
            <CommandList className="max-h-[200px]">
              <CommandEmpty className="py-3 text-xs">
                No input found.
              </CommandEmpty>
              <CommandGroup>
                {inputs.map((input) => (
                  <CommandItem
                    key={input.index}
                    value={input.label}
                    onSelect={() => {
                      onRoute?.(output.index, input.index);
                      setOpen(false);
                    }}
                    className="text-xs"
                  >
                    <Check
                      className={cn(
                        "mr-1.5 h-3 w-3",
                        output.source === input.index
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {input.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Button View — Destination→Source button picker               */
/* ─────────────────────────────────────────────────────────── */

const BUTTONS_PER_GROUP = 10;

function groupItems<T extends { index: number }>(
  items: T[],
): { label: string; items: T[] }[] {
  const groups: { label: string; items: T[] }[] = [];
  for (let i = 0; i < items.length; i += BUTTONS_PER_GROUP) {
    const chunk = items.slice(i, i + BUTTONS_PER_GROUP);
    groups.push({
      label: `${i + 1}–${Math.min(i + BUTTONS_PER_GROUP, items.length)}`,
      items: chunk,
    });
  }
  return groups;
}

interface RoutingButtonPanelProps {
  inputs: VideoHubInput[];
  outputs: VideoHubOutput[];
  onRoute?: (output: number, input: number) => void;
  disabled?: boolean;
}

const RoutingButtonPanel = ({
  inputs,
  outputs,
  onRoute,
  disabled,
}: RoutingButtonPanelProps) => {
  const [selectedDest, setSelectedDest] = useState<number | null>(null);
  const [expandedDestGroup, setExpandedDestGroup] = useState<number>(0);
  const [expandedSrcGroup, setExpandedSrcGroup] = useState<number>(0);

  const selectedOutput =
    selectedDest !== null
      ? outputs.find((o) => o.index === selectedDest)
      : null;

  const handleSourceSelect = (inputIndex: number) => {
    if (selectedDest !== null && onRoute) {
      onRoute(selectedDest, inputIndex);
    }
  };

  const destGroups = groupItems(outputs);
  const srcGroups = groupItems(inputs);
  const needsGrouping = outputs.length > BUTTONS_PER_GROUP;

  return (
    <div className="space-y-4">
      {/* Destinations (Outputs) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Destinations
          </CardTitle>
        </CardHeader>
        <CardContent>
          {needsGrouping ? (
            <div className="space-y-3">
              {destGroups.map((group, gi) => (
                <div key={gi}>
                  <button
                    className="text-xs font-medium text-muted-foreground mb-1.5 hover:text-foreground transition-colors"
                    onClick={() => setExpandedDestGroup(gi)}
                  >
                    {group.label}
                    {expandedDestGroup === gi ? " ▾" : " ▸"}
                  </button>
                  {expandedDestGroup === gi && (
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
                      {group.items.map((output) => (
                        <DestButton
                          key={output.index}
                          output={output}
                          currentSource={inputs.find(
                            (i) => i.index === output.source,
                          )}
                          isSelected={selectedDest === output.index}
                          onClick={() => setSelectedDest(output.index)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
              {outputs.map((output) => (
                <DestButton
                  key={output.index}
                  output={output}
                  currentSource={inputs.find((i) => i.index === output.source)}
                  isSelected={selectedDest === output.index}
                  onClick={() => setSelectedDest(output.index)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sources (Inputs) — shown when a destination is selected */}
      {selectedOutput && (
        <Card className="border-primary/50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Sources → {selectedOutput.label}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-6"
                onClick={() => setSelectedDest(null)}
              >
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {needsGrouping ? (
              <div className="space-y-3">
                {srcGroups.map((group, gi) => (
                  <div key={gi}>
                    <button
                      className="text-xs font-medium text-muted-foreground mb-1.5 hover:text-foreground transition-colors"
                      onClick={() => setExpandedSrcGroup(gi)}
                    >
                      {group.label}
                      {expandedSrcGroup === gi ? " ▾" : " ▸"}
                    </button>
                    {expandedSrcGroup === gi && (
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
                        {group.items.map((input) => (
                          <SrcButton
                            key={input.index}
                            input={input}
                            isActive={selectedOutput.source === input.index}
                            onClick={() => handleSourceSelect(input.index)}
                            disabled={disabled || !onRoute}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
                {inputs.map((input) => (
                  <SrcButton
                    key={input.index}
                    input={input}
                    isActive={selectedOutput.source === input.index}
                    onClick={() => handleSourceSelect(input.index)}
                    disabled={disabled || !onRoute}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Destination Button                                          */
/* ─────────────────────────────────────────────────────────── */

interface DestButtonProps {
  output: VideoHubOutput;
  currentSource?: VideoHubInput;
  isSelected: boolean;
  onClick: () => void;
}

const DestButton = ({
  output,
  currentSource,
  isSelected,
  onClick,
}: DestButtonProps) => (
  <button
    className={cn(
      "flex flex-col items-start p-2 rounded-md border text-left transition-colors min-h-[52px]",
      isSelected
        ? "bg-primary text-primary-foreground border-primary"
        : "bg-card hover:bg-accent border-border",
    )}
    onClick={onClick}
  >
    <span className="text-[11px] font-medium truncate w-full">
      {output.label}
    </span>
    <span
      className={cn(
        "text-[10px] truncate w-full",
        isSelected ? "text-primary-foreground/70" : "text-muted-foreground",
      )}
    >
      ← {currentSource?.label ?? `Input ${output.source + 1}`}
    </span>
  </button>
);

/* ─────────────────────────────────────────────────────────── */
/* Source Button                                                */
/* ─────────────────────────────────────────────────────────── */

interface SrcButtonProps {
  input: VideoHubInput;
  isActive: boolean;
  onClick: () => void;
  disabled?: boolean;
}

const SrcButton = ({ input, isActive, onClick, disabled }: SrcButtonProps) => (
  <button
    className={cn(
      "flex items-center justify-center p-2 rounded-md border text-center transition-colors min-h-[44px]",
      isActive
        ? "bg-green-600 text-white border-green-600"
        : "bg-card hover:bg-accent border-border",
      disabled && "opacity-50 cursor-not-allowed",
    )}
    onClick={onClick}
    disabled={disabled}
  >
    <span className="text-[11px] font-medium truncate">{input.label}</span>
  </button>
);

/* ─────────────────────────────────────────────────────────── */
/* Label Row - Editable label for input/output                */
/* ─────────────────────────────────────────────────────────── */

interface LabelRowProps {
  index: number;
  label: string;
  onSave?: (index: number, label: string) => void;
  disabled?: boolean;
}

const LabelRow = ({ index, label, onSave, disabled }: LabelRowProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(label);

  const handleSave = () => {
    if (editValue !== label) {
      onSave?.(index, editValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(label);
    setIsEditing(false);
  };

  return (
    <TableRow data-testid={`label-row-${index}`}>
      <TableCell className="font-mono text-muted-foreground">
        {index + 1}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") handleCancel();
            }}
            className="h-8"
            autoFocus
          />
        ) : (
          <span>{label}</span>
        )}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={handleSave}
              disabled={disabled}
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon-sm" onClick={handleCancel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setIsEditing(true)}
            disabled={disabled || !onSave}
          >
            <Edit2 className="h-4 w-4" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};
